<script setup lang="ts">
import { mdiLoading, mdiMagnify } from "@mdi/js";
import { useQuery } from "@tanstack/vue-query";
import { useLazyQuery } from "@vue/apollo-composable";
import { storeToRefs } from "pinia";
import type { Ref } from "vue";
import SearchAutoComplete from "~/components/layout/Search/SearchAutoComplete.vue";
import SearchResults from "~/components/layout/Search/SearchResults.vue";
import PopOver from "~/components/ui/Popover/PopOver.vue";
import useFeeds from "~/composables/Feeds/useFeeds";
import { signalREndpoints, useSignalR } from "~/composables/SignalR/useSignalR";
import EntitySearch from "~/graphql/queries/EntityPage.graphql";
import PopularSearchers from "~/graphql/queries/PopularSearches.graphql";
import SearchCompletions from "~/graphql/queries/SearchCompletions.graphql";
import SearchDomain from "~/graphql/queries/SearchDomain.graphql";
import SearchResultItem from "~/graphql/queries/SearchResultItem.graphql";
import { useAuth } from "~/store/auth";
import { useBetslip } from "~/store/betslip";
import { useSettings } from "~/store/settings";
import { IMarketHeaderResponse } from "~/types/config/marketHeader";
import { IGame } from "~/types/gaming/game";
import { IGetAllSubscriptionsResponse } from "~/types/graph-ql/feeds-types";
import type {
	IDomainSearch,
	IEntitySearch,
	IEntityTop,
	IPopularSearches,
	ISearchCompletions,
	ISearchEntity,
	ISearchItem,
} from "~/types/graph-ql/graphql-types";
import type { IEvent } from "~/types/sports/event";
import type { IMarket } from "~/types/sports/market";
import type { IOutcome } from "~/types/sports/outcome";
import type { IPrice } from "~/types/sports/price";
import type { IScore } from "~/types/sports/score";
import type { ISport } from "~/types/sports/sport";
import api from "~/utils/auth/api";
import validateVertical from "~/utils/casino/validateVertical";
import { isMobile } from "~/utils/constants";
import generateSubscriptionQuery from "~/utils/search/allSubscriptionsQuery";
import useDebounce from "~/utils/search/debounce";
import { extractMarketsAndEventIds } from "~/utils/search/searchUtil";
import t from "~/utils/translate";

export type EventQuery = {
	events: EventQueryEvent[];
};

export type EventQueryEvent = {
	displayName: string;
	eventId: number;
	isLive: boolean;
	league: string;
	markets: [];
	outcomes: [];
	region: string;
	resultType: number;
	sport: string;
	startDateTime: string;
	startTimeEpoch: number;
};

interface Props {
	homePage?: boolean;
	searchValue?: string;
	submitSearch?: boolean;
}

const props = defineProps<Props>();
const emits = defineEmits(["searchSubmitted", "updateSearchValue"]);

const router = useRouter();
const route = useRoute();
const config = useRuntimeConfig();
const debounce = useDebounce();
const settings = useSettings();
const betSlip = useBetslip();
const { recentSearches } = storeToRefs(betSlip);
const followingsArray: Ref<IEntityTop[] | null> = ref(null);
const searchTerm: Ref<string | null> = ref(null);
const feedsSearchLoading = ref(false);
const { followedItems } = useFeeds();
const { $trackEvent } = useNuxtApp();
const auth = useAuth();

const term: Ref<string> = ref("");
const { showSearch } = storeToRefs(settings);

const loadingCompletions = ref(false);
const completions = ref<ISearchEntity[] | undefined>();

const filterMarkets = ref<{ name: string; displayName: string; filterIndex: number }[] | undefined>(undefined);
const activeMarket = ref<string | undefined>();
const ukEventResultsId = ref<string[]>([]);
const radarEventResultsId = ref<string[]>([]);

const searchLoading = ref(false);

const emopLoading = ref(false);
const emopData = ref();

const searchFilterOptions = ["all", "sport", "casino"];
const activeSearchFilter = ref<string>("all");

const casinoData = ref<IGame[]>([]);
const casinoLoading = ref<boolean>(false);
const casinoError = ref<unknown>();
const searchFocused = ref<boolean>(false);

const showFeedsAndFollowings = computed(() => {
	return settings.hasFeature("EnableFeedsAndFollow") && auth.loggedIn;
});

const setNoResult = () => {
	const events: Map<number, IEvent> = new Map<number, IEvent>();
	const markets: Map<number, IMarket> = new Map<number, IMarket>();
	const outcomes: Map<number, IOutcome> = new Map<number, IOutcome>();
	const prices: Map<number, IPrice> = new Map<number, IPrice>();
	emopData.value = { markets, outcomes, prices, events };
	recentSearches.value = recentSearches.value.filter((search) => search.name !== term.value);
	searchLoading.value = false;
	emopLoading.value = false;
	loadingCompletions.value = false;
};

const { data: sports } = useQuery<{ sports: ISport[] }>({
	queryKey: ["sports"],
	queryFn: () =>
		api({
			url: "/cron/sports/{brand}/{locale}",
			options: {
				method: "GET",
			},
		}),
});

const { data: headers } = useQuery<IMarketHeaderResponse>({
	queryKey: ["sports-market-headers"],
	queryFn: () =>
		api({
			url: `/cron/sports-book/market-header-config/synapse/{brand}`,
			type: "config",
			options: {
				method: "GET",
			},
		}),
});

//Popular Searches
const {
	result: popularSearches,
	loading: loadingPopularSearches,
	load: getPopular,
} = useLazyQuery<IPopularSearches>(
	gql`
		${PopularSearchers}
	`,
	{
		types: ["TEAM", "PLAYER", "CASINO_GAME", "COMPETITION", "LINK"],
		limit: 10,
	},
	{
		context: settings.isRadarEnabled ? { headers: { Authorization: "" } } : {},
	}
);

//Suggestions
const { onResult: onCompletionsResult, load: getCompletions } = useLazyQuery<ISearchCompletions>(
	gql`
		${SearchCompletions}
	`,
	{
		query: term.value,
		modelType: "SEARCH_SPORT",
		searchResultTypes: ["COMPETITION", "PARTICIPANT"],
	},
	{
		context: settings.isRadarEnabled ? { headers: { Authorization: "" } } : {},
		fetchPolicy: "no-cache",
	}
);

onCompletionsResult((result) => {
	if (result.data) {
		completions.value = result.data.search.completions;
		loadingCompletions.value = false;
	}
});

//Entity Search
const entitySearchVars = ref<{ entity: string } | null>(null);

const { load: getEntitySearch } = useLazyQuery<ISearchItem>(
	gql`
		${EntitySearch}
	`,
	entitySearchVars,
	{
		keepPreviousResult: false,
		context: settings.isRadarEnabled ? { headers: { Authorization: "" } } : {},
		fetchPolicy: "no-cache",
	}
);

//Domain Search
const domainSearchVars = ref<{
	query: string;
	searchResultTypes: string[];
} | null>(null);

const {
	onError: onDomainSearchError,
	load: getDomainSearch,
	refetch: refetchDomainSearch,
} = useLazyQuery<IDomainSearch>(
	gql`
		${SearchDomain}
	`,
	null,
	{
		keepPreviousResult: false,
		context: settings.isRadarEnabled ? { headers: { Authorization: "" } } : {},
		fetchPolicy: "no-cache",
	}
);

const getEventSearch = () => {
	return api<EventQueryEvent[]>({
		url: `/Search/EventSearch?searchQuery=${term.value}&Country={brand}&Paging.Skip=0&Paging.Take=100&IncludeMarketsAndOutcomes=true`,
		type: "sports",
		apiVersion: "v1",
	});
};

const handleMergeMarkets = (markets: { name: string; displayName: string; filterIndex: number }[]) => {
	if (!filterMarkets.value) {
		filterMarkets.value = [];
	}

	for (const market of markets) {
		if (!filterMarkets.value?.some((item1) => item1.name === market.name)) {
			filterMarkets.value.push(market);
		}
	}
};

const doSearch = async (isEntitySearch = false) => {
	try {
		reset();
		searchLoading.value = true;
		const [domainResult, entityResult, eventResult] = await Promise.all([
			!isEntitySearch ? getDomainSearch(null, domainSearchVars.value) || refetchDomainSearch(domainSearchVars.value) : undefined,
			isEntitySearch ? getEntitySearch() : undefined,
			getEventSearch(),
		]);

		if (domainResult || eventResult || entityResult) {
			if (eventResult) {
				const eventSearchEventsMarkets = extractMarketsAndEventIds({ events: eventResult }, sports.value?.sports);
				handleMergeMarkets(eventSearchEventsMarkets.markets);
				radarEventResultsId.value = eventSearchEventsMarkets.eventIds;
			}

			if (domainResult) {
				const domainSearchEventsMarkets = await handleDomainSearchResult("data" in domainResult ? domainResult.data : domainResult);
				handleMergeMarkets(domainSearchEventsMarkets.markets);
				ukEventResultsId.value = domainSearchEventsMarkets.eventIds;
			}

			if (entityResult) {
				const entitySearchEventsMarkets = extractMarketsAndEventIds(entityResult?.offer, sports.value?.sports);
				handleMergeMarkets(entitySearchEventsMarkets.markets);
				ukEventResultsId.value = entitySearchEventsMarkets.eventIds;
			}

			if (filterMarkets.value && filterMarkets.value.length > 0) {
				activeMarket.value = filterMarkets.value[0].name;
				await getEmop(filterMarkets.value[0].name);
			} else {
				setNoResult();
			}
		}
	} catch (e) {
		console.log(e);
	} finally {
		loadingCompletions.value = false;
		searchLoading.value = false;
	}
};

const handleDomainSearchResult = async (result?: IDomainSearch) => {
	const combinedData: { eventIds: string[]; markets: { name: string; displayName: string; filterIndex: number }[] } = {
		markets: [],
		eventIds: [],
	};
	if (result && result.search.search.length > 0) {
		const calls = result.search.search.map(async (entity) => {
			if (!followingsArray.value) {
				followingsArray.value = [entity];
			} else {
				followingsArray.value.push(entity);
			}

			const { load } = useLazyQuery<IEntitySearch>(
				gql`
					${SearchResultItem}
				`,
				{
					entity: entity.id,
				},
				{
					fetchPolicy: "no-cache",
					context: settings.isRadarEnabled ? { headers: { Authorization: "" } } : {},
				}
			);

			return load();
		});

		await Promise.all(calls).then((results) => {
			for (const result of results) {
				if (!result) return;
				const { markets, eventIds } = extractMarketsAndEventIds(result?.offer, sports.value?.sports);
				combinedData.eventIds = Array.from(new Set([...combinedData.eventIds, ...eventIds]));
				combinedData.markets = combinedData.markets.concat(
					markets.filter((item2) => !combinedData.markets?.some((item1) => item1.name === item2.name))
				);
			}
		});
	}

	return combinedData;
};
onDomainSearchError(() => {
	setNoResult();
});

const buildEmopCalls = (searchEventsQueryString: string, market: string, transform: boolean) => {
	return api<
		{
			event: IEvent;
			markets: IMarket[];
			outcomes: IOutcome[];
			prices: IPrice[];
		}[]
	>({
		url: `/Feeds/EMOP?${searchEventsQueryString}&marketNames=${market}&countryCode={brand}&cultureCode={locale}${
			transform ? "&isTransformationRequired=true" : ""
		}`,
		type: "sports",
		apiVersion: "v3",
	});
};

//Emop
const getEmop = async (market: string, limit = 10) => {
	let offset = 0;
	emopLoading.value = true;
	const emopCalls = [];

	while (ukEventResultsId.value.length > offset) {
		const eventIdsChunk = ukEventResultsId.value.slice(offset, offset + limit);
		const searchEventsQueryString = eventIdsChunk.map((eventId) => `eventIds=${eventId}&`).join("");
		offset += limit;
		emopCalls.push(buildEmopCalls(searchEventsQueryString, market, true));
	}

	offset = 0;
	while (radarEventResultsId.value.length > offset) {
		const eventIdsChunk = radarEventResultsId.value.slice(offset, offset + limit);
		const searchEventsQueryString = eventIdsChunk.map((eventId) => `eventIds=${eventId}&`).join("");
		offset += limit;
		emopCalls.push(buildEmopCalls(searchEventsQueryString, market, false));
	}

	const events = new Map<number, IEvent>();
	const markets = new Map<string, IMarket>();
	const outcomes = new Map<string, IOutcome>();
	const prices = new Map<string, IPrice>();

	return Promise.all(emopCalls)
		.then((results) => {
			for (const resp of results) {
				if (resp.length) {
					try {
						for (const result of resp) {
							events.set(result.event.eventId, result.event);
							result.markets.forEach((m) => markets.set(m.marketId, m));
							result.outcomes.forEach((o) => outcomes.set(o.outcomeId, o));
							result.prices.forEach((p) => prices.set(p.outcomeId, p));
						}
					} catch (e) {
						console.log(e);
					}
				}
			}
			const eventIdArray = Array.from(events.keys());
			const payload = eventIdArray.map((eventId) => `event::${eventId}`);
			searchSignalR.subscribe(signalREndpoints.subscriptionEventsEndpoint, eventIdArray, payload);
			emopData.value = { markets, outcomes, prices, events };
			return emopData.value;
		})
		.catch((e) => console.log(e))
		.finally(() => {
			emopLoading.value = false;
		});
};

const switchMarket = async (marketName: string) => {
	const market = filterMarkets.value?.find((fm) => fm.name === marketName);
	if (market) {
		activeMarket.value = market.name;
		const payload = Array.from(emopData.value.events.keys()).map((eventId) => `event::${eventId}`);
		searchSignalR.unSubscribe(signalREndpoints.unsubscriptionEventsEndpoint, payload);
		await getEmop(market.name);
	}
};

//SearchItem
const handleSearch = (val: KeyboardEvent | IEntityTop) => {
	searchLoading.value = true;
	emopLoading.value = true;
	if (val instanceof KeyboardEvent) {
		if (term.value === "") {
			searchLoading.value = false;
			emopLoading.value = false;
			return;
		}
		addRecentSearch(term.value);
	} else {
		term.value = (val as IEntityTop).name;
	}
	domainSearchVars.value = {
		query: term.value,
		searchResultTypes: ["COMPETITION", "PARTICIPANT"],
	};
	doSearch();

	$trackEvent.searchQuery(term.value);
	if (props.homePage) handleCasinoSearch();
};

const addRecentSearch = (searchTerm: string | IEntityTop) => {
	if (typeof searchTerm === "string") {
		if (
			!popularSearches.value?.entity.top.find((suggestion) => suggestion.name === searchTerm) &&
			!recentSearches.value.find((recent) => recent.name === searchTerm)
		) {
			recentSearches.value.push({
				name: searchTerm,
				id: null,
			});
		}
	} else {
		if (
			!popularSearches.value?.entity.top.find((suggestion) => suggestion.id === searchTerm.id) &&
			!recentSearches.value.find((recent) => recent.id === searchTerm.id)
		) {
			recentSearches.value.push({
				name: searchTerm.name,
				id: searchTerm.id,
				type: searchTerm.type,
			});
		}
	}
};

const reset = () => {
	emopData.value = undefined;
	followingsArray.value = null;
	ukEventResultsId.value = [];
	radarEventResultsId.value = [];
	filterMarkets.value = undefined;
	loadingCompletions.value = false;
	searchLoading.value = false;
	casinoData.value = [];
};

const initAutoComplete = debounce((e: KeyboardEvent) => {
	if (term.value === "" && e.key === "Backspace") {
		completions.value = undefined;
		reset();
	} else if (term.value.length >= 3) {
		reset();
		searchTerm.value = term.value;
		loadingCompletions.value = true;
		getCompletions(null, {
			query: term.value,
			modelType: "SEARCH_SPORT",
			searchResultTypes: ["COMPETITION", "PARTICIPANT"],
		});
	} else {
		handleShow();
	}
}, 300);

const autoComplete = (e: KeyboardEvent) => {
	if (e.ctrlKey || (e.ctrlKey && e.key === "a")) {
		return;
	}
	initAutoComplete(e);
};

const eventsCallback = (listener: string, data: IEvent | IMarket | IOutcome | IPrice | IScore) => {
	if (emopData.value) {
		switch (listener) {
			case "event":
				if (emopData.value.events.get(data.eventId)) emopData.value.events.set(data.eventId, data as IEvent);
				break;
			case "market":
				if ("marketId" in data) {
					emopData.value.markets.value.set(data.marketId, data as IMarket);
				}
				break;
			case "outcome":
				if ("outcomeId" in data) {
					emopData.value.outcomes.set(data.outcomeId, data as IOutcome);
				}
				break;
			case "price":
				if ("outcomeId" in data) {
					emopData.value.prices.set(data.outcomeId, data as IPrice);
				}
				break;
		}
	}
};
const searchSignalR = useSignalR("search", eventsCallback);

const handleShow = () => {
	showSearch.value = true;
	$trackEvent.toggleSearch();
};

// If logged in get users current subscription

const { data: currentFollowings, refetch: refetchFollowings } = useQuery({
	queryKey: ["get-subscriptions"],
	queryFn: () =>
		api<IGetAllSubscriptionsResponse>({
			url: "",
			type: "graphQl",
			options: { method: "POST" },
			data: generateSubscriptionQuery(),
		}).then((resp) => {
			followedItems.value = resp.data.subscription.subscriptions.map((item) => item.identifier);
			return resp.data.subscription.subscriptions;
		}),
	initialData() {
		return [];
	},
	enabled: computed(() => showFeedsAndFollowings.value),
	refetchOnMount: false,
});

provide("getFollowings", refetchFollowings);

const closeResults = () => {
	term.value = "";
	completions.value = undefined;
	if (emopData.value) {
		const payload = Array.from(emopData.value.events.keys()).map((eventId) => `event::${eventId}`);
		searchSignalR.unSubscribe(signalREndpoints.unsubscriptionEventsEndpoint, payload);
	}
	emopData.value = undefined;
	searchTerm.value = "";
	handleClose();
};

const modalTitle = computed(() => {
	if (isMobile) {
		return t("search");
	}
	if (emopData.value || emopLoading.value || feedsSearchLoading.value || searchLoading.value) {
		return t("results");
	}
	return undefined;
});

const showAutoComplete = computed(() => {
	return !emopData.value && !feedsSearchLoading.value && !searchLoading.value && !emopLoading.value;
});

onBeforeRouteLeave(() => {
	showSearch.value = false;
});

watchEffect(() => {
	if (route.query.search) {
		showSearch.value = true;
	}
});

const handleClose = () => {
	showSearch.value = false;
	const copy = route.query;
	//@ts-ignore
	delete copy["search"];
	router.push({ query: copy });
};

const opened = () => {
	if (!popularSearches.value) {
		getPopular();
	}
};

const handleCasinoSearch = () => {
	casinoData.value = [];
	casinoLoading.value = true;
	casinoError.value = false;

	const casinoVertical =
		settings?.getSitemap
			?.filter((x) => x.N.top)
			.find((x) => x.RURL.includes("lobby"))
			?.RURL.split("/")[2] ?? "casino";

	api<{ data: IGame[] }>({
		url: `/Gaming/Search?search=${term.value}&languageCode={locale}&channel={channel}&skip=0&limit=15&currency={currency}&regionCode=${
			settings.config?.brandSuffix as string
		}&vertical=${validateVertical(casinoVertical)}&environment=${config.public.casinoEnv}`,
		type: "casinoApiCdn",
		options: {
			method: "GET",
		},
	})
		.then((res) => {
			casinoLoading.value = false;
			if (res.data.length > 0) casinoData.value = res.data.slice(0, 30);
			else casinoError.value = true;
		})
		.catch(() => {
			casinoLoading.value = false;
			casinoError.value = true;
		})
		.finally(() => setActiveOption());
};

const setActiveOption = () => {
	if (!casinoLoading.value && !searchLoading.value && !feedsSearchLoading.value && !emopLoading.value) {
		if (emopData.value && emopData.value.markets.size > 0 && casinoData.value.length <= 0) activeSearchFilter.value = "sport";
		if ((!emopData.value || emopData.value.markets.size <= 0) && casinoData.value && casinoData.value.length > 0) activeSearchFilter.value = "casino";
		if (!!emopData.value && emopData.value.markets.size > 0 && casinoData.value.length > 0) activeSearchFilter.value = "all";
		if (!!emopData.value && emopData.value.markets.size <= 0 && casinoData.value.length <= 0) activeSearchFilter.value = "all";
	}
};

const getOptionColor = (option: string) => {
	if (option === "sport") return "bg-primary-600";
	if (option === "casino") return "bg-casino-800";
	return "bg-primary-600";
};

watchEffect(() => {
	if (!searchFocused.value && props.homePage) {
		term.value = props.searchValue || "";
	} else {
		emits("updateSearchValue", term.value);
	}

	if (term.value && props.submitSearch) {
		reset();
		handleSearch({ name: term.value } as IEntityTop);
		emits("searchSubmitted");
	}

	setActiveOption();
});

const handleShowCheck = () => {
	if (!searchFocused.value || !showSearch.value) {
		searchFocused.value = true;
		showSearch.value = true;
	}
};
</script>

<template>
	<PopOver
		v-if="!homePage"
		v-model="showSearch"
		@opened="opened"
		@close="closeResults"
		@clickout="closeResults"
		contentKey="search"
		:styles="{
			contentInner: 'p-2 dark:bg-dark-900 drop-shadow-sm h-full overflow-y-auto max-h-[calc(100vh-340px)] bg-light-200',
			wrapper: 'flex-grow relative',
			modelWrapper: 'absolute top-[45px] left-0 right-0 overflow-hidden max-h-[calc(100vh-300px)] h-max min-h-[300px]',
			modalContent: 'dark:bg-dark-900',
			header: 'text-light-50 py-2 px-4 bg-dark-700 flex justify-between font-bold',
			content: [
				'bg-light-50 overflow-hidden h-full flex flex-col',
				!emopData && !searchLoading && !feedsSearchLoading && !emopLoading ? 'rounded-b-2xl' : 'rounded-2xl',
			],
		}"
		:title="modalTitle"
	>
		<template #trigger>
			<UiFormInput
				type="text"
				:clearInputIcon="!loadingCompletions && !searchLoading"
				no-floating-label
				v-model="term"
				:focus-on-clear="true"
				:label="t('find-events-teams')"
				:class="['text-xs', homePage ? 'block' : 'hidden md:block']"
				:autocomplete="false"
				:icon-class="[loadingCompletions || searchLoading ? 'animate-spin' : '']"
				:icon="loadingCompletions || searchLoading ? mdiLoading : term.length > 0 ? '' : mdiMagnify"
				iconPos="right"
				@update:clear-input="
					completions = undefined;
					emopData = undefined;
				"
				@focusin="handleShow()"
				@focus-out="
					showSearch = false;
					searchFocused = false;
				"
				@keydown="handleShowCheck"
				@keydown.enter="handleSearch"
			/>
		</template>
		<template #default>
			<div class="absolute top-0 left-0 right-0 p-2 bg-light-50 dark:bg-dark-800 lg:hidden">
				<UiFormInput
					type="text"
					:clearInputIcon="!loadingCompletions && !searchLoading"
					no-floating-label
					v-model="term"
					:focus-on-clear="true"
					:label="'Find events or teams'"
					class="text-xs lg:hidden"
					:autocomplete="false"
					:icon-class="[loadingCompletions || searchLoading ? 'animate-spin' : '']"
					:icon="loadingCompletions || searchLoading ? mdiLoading : term.length > 0 ? '' : mdiMagnify"
					iconPos="right"
					@update:clear-input="
						completions = undefined;
						emopData = undefined;
					"
					@focusin="handleShow()"
					@focus-out="
						showSearch = false;
						searchFocused = false;
					"
					@keydown="handleShowCheck"
					@keydown.enter="handleSearch"
				/>
			</div>
			<div class="mt-[50px] lg:mt-0 dark:bg-dark-900">
				<SearchAutoComplete
					v-show="showAutoComplete"
					@select="handleSearch"
					:completions="completions"
					:loading-popular-searches="loadingPopularSearches"
					:popular-searches="popularSearches"
				/>
				<SearchResults
					@switch-markets="switchMarket"
					v-if="!showAutoComplete || searchLoading"
					:emopData="emopData"
					:active-market="activeMarket"
					:filter-markets="filterMarkets"
					:marketHeaders="headers?.sports"
					:loading="searchLoading || feedsSearchLoading"
					:resultsLoading="emopLoading"
					:followings="followingsArray"
					:currentFollowings="currentFollowings"
				/>
			</div>
		</template>
	</PopOver>

	<div v-else>
		<UiFormInput
			type="text"
			:clearInputIcon="!loadingCompletions"
			no-floating-label
			v-model="term"
			:focus-on-clear="true"
			:label="t('find-events-teams')"
			class="text-xs block"
			:autocomplete="false"
			:icon-class="[loadingCompletions || searchLoading ? 'animate-spin' : '']"
			:icon="loadingCompletions || searchLoading ? mdiLoading : term.length > 0 ? '' : mdiMagnify"
			iconPos="right"
			@focusin="
				handleShow();
				searchFocused = true;
			"
			@focus-out="
				showSearch = false;
				searchFocused = false;
			"
			@keydown.enter="handleSearch"
		/>
		<div class="flex gap-2 my-3">
			<label
				v-for="option in searchFilterOptions"
				@click="activeSearchFilter = option"
				:class="[
					'rounded-lg px-3 py-1 font-bold cursor-pointer text-sm leading-5',
					activeSearchFilter === option ? ['text-light-50', getOptionColor(option)] : '',
				]"
			>
				{{ t(option) }}
			</label>
		</div>
		<SearchResults
			@switch-markets="switchMarket"
			:emopData="emopData"
			:active-market="activeMarket"
			:filter-markets="filterMarkets"
			:loading="searchLoading || feedsSearchLoading"
			:resultsLoading="emopLoading"
			:followings="followingsArray"
			:currentFollowings="currentFollowings"
			:marketHeaders="headers?.sports"
			homePage
			:search-filters="activeSearchFilter"
			:casino-data="casinoData"
			:casino-loading="casinoLoading"
			:casino-error="casinoError"
		/>
	</div>
</template>
